<script setup lang="ts">
import { computed } from "vue";
import { Tooltip } from "@/components/ui/tooltip";
import { renderMarkdownInline } from "@utils/renderMarkdown";
import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";

type HeatmapRow = Record<string, unknown> & { itemLabel?: string; groupLabel?: string; itemN?: number };

interface HeatmapCell {
  code: string;
  label: string;
  value: number | null;
  count: number | null;
}

const props = withDefaults(
  defineProps<{
    data: HeatmapRow[];
    choiceCodes: string[];
    choiceLabels: Record<string, string>;
    accent?: string | null;
  }>(),
  { accent: null },
);

const rows = computed(() =>
  props.data.map((row) => ({
    label: row.groupLabel || row.itemLabel || "",
    n: row.itemN,
    cells: props.choiceCodes.map<HeatmapCell>((code) => ({
      code,
      label: props.choiceLabels[code] || code,
      value: (row[`choice_${code}`] as number | undefined) ?? null,
      count: (row[`count_${code}`] as number | undefined) ?? null,
    })),
  })),
);

const maxValue = computed(() =>
  Math.max(0, ...rows.value.flatMap((row) => row.cells.map((cell) => cell.value || 0))),
);

const accentRgb = computed(() => {
  const hex = /^#[0-9a-f]{6}$/i.test(props.accent || "") ? (props.accent as string) : POPULATION_AUTO_COLORS[0];
  return [hex.slice(1, 3), hex.slice(3, 5), hex.slice(5, 7)].map((part) => parseInt(part, 16));
});

const gridStyle = computed(() => ({
  gridTemplateColumns: `minmax(0, max-content) 2.5rem repeat(${props.choiceCodes.length}, minmax(0, 1fr))`,
}));

function cellStyle(cell: HeatmapCell) {
  if (cell.value == null || !maxValue.value) return {};
  const ratio = cell.value / maxValue.value;
  const [r, g, b] = accentRgb.value;
  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, ${(ratio * 0.9).toFixed(3)})`,
    color: ratio > 0.55 ? "white" : undefined,
  };
}

function formatPercent(value: number | null) {
  return value == null ? "–" : `${Math.round(value * 100)}%`;
}
</script>

<template>
  <div class="grid gap-px text-2xs font-medium leading-[15px]" :style="gridStyle">
    <div />
    <div class="self-end p-1 text-center text-muted-foreground">N</div>
    <div
      v-for="code in choiceCodes"
      :key="`h-${code}`"
      class="self-end p-1 text-center">
      <span
        class="line-clamp-2 break-words"
        :title="choiceLabels[code] || code"
        v-html="renderMarkdownInline(choiceLabels[code] || code)" />
    </div>

    <template v-for="(row, rowIdx) in rows" :key="rowIdx">
      <div class="flex max-w-[160px] items-center justify-end p-1 pr-2 text-right">
        <span
          class="line-clamp-2 break-words"
          :title="row.label"
          v-html="renderMarkdownInline(row.label)" />
      </div>
      <div class="flex items-center justify-center p-1 tabular-nums text-muted-foreground">{{ row.n }}</div>
      <Tooltip
        v-for="cell in row.cells"
        :key="`${rowIdx}-${cell.code}`"
        :delay-duration="0"
        :disabled="cell.value == null"
        trigger-class="!cursor-default min-w-0">
        <div
          class="flex h-9 items-center justify-center tabular-nums transition-colors duration-300"
          :style="cellStyle(cell)">
          {{ formatPercent(cell.value) }}
        </div>
        <template #content>
          <slot name="tooltip" :row="row" :cell="cell">
            <div class="flex flex-col gap-0.5 text-xs">
              <span class="font-semibold" v-html="renderMarkdownInline(row.label)" />
              <span v-html="renderMarkdownInline(cell.label)" />
              <span class="tabular-nums text-muted-foreground">
                {{ formatPercent(cell.value) }}<template v-if="cell.count != null"> · n={{ Math.round(cell.count) }}</template>
              </span>
            </div>
          </slot>
        </template>
      </Tooltip>
    </template>
  </div>
</template>
